import {
  cloneElement,
  ForwardedRef,
  forwardRef,
  PropsWithChildren,
  ReactNode,
} from 'react';
import { mergeProps, useRender, UseRenderRenderProp } from '@base-ui/react';

import { getArticle, Link, Tooltip } from '@components/primitives';

import { useAllow } from '@components/features/Restricted/access';
import {
  getRole,
  RoleRegistryKey,
} from '@components/features/Restricted/registry';
import format from '@utils/format';

import css from './styles.module.css';

export interface PermissionsTooltipProps {
  allow: RoleRegistryKey | RoleRegistryKey[];
  title?: ReactNode;
  teamUuid?: string;
}

export function PermissionsTooltip({
  allow,
  title,
  teamUuid,
}: PermissionsTooltipProps) {
  const roleKeys = Array.isArray(allow) ? allow : [allow];
  const roles = roleKeys.flatMap((key) => {
    const role = getRole(key);
    return role.type === 'role' ? [role] : role.roles;
  });
  const roleLabels = roles.map((r) => r.label);
  const roleList = format.list(roleLabels, 'or') ?? '';

  const hasTeamContext = roles.some((r) => r.context === 'team');

  return (
    <div className={css.tooltip}>
      <p>{title || 'You do not have enough permissions'}</p>
      <p>
        You must be {getArticle(roleList)} {roleList} to perform this action.
        {hasTeamContext && (
          <>
            {' '}
            Contact a{' '}
            <Link href={`/${teamUuid}/settings/team/members`}>
              team admin
            </Link>{' '}
            to update your role.
          </>
        )}
      </p>
    </div>
  );
}

export interface RestrictedProps extends PropsWithChildren {
  /**
   * Which roles are allowed to access the component.
   * Pass an array of roles (or a role group) to allow multiple roles.
   *
   * @example <Restricted allow="support" />
   * @example <Restricted allow={['support', 'admin']} />
   */
  allow: RoleRegistryKey | RoleRegistryKey[];
  /**
   * An optional team UUID to check permissions for.
   * @default `useParams().teamUuid`
   */
  teamUuid?: string;
  /**
   * Which element to disable when the user does not have access.
   * @example <Restricted allow="superuser" render={<Button />} />
   */
  render: UseRenderRenderProp<{ disabled: boolean }>;

  // Customize tooltip position and content
  align?: 'start' | 'center' | 'end';
  alignOffset?: number;
  side?: 'top' | 'bottom' | 'left' | 'right';
  sideOffset?: number;
  message?: ReactNode;
}

export const Restricted = forwardRef<HTMLElement, RestrictedProps>(
  function Restricted(
    {
      allow,
      teamUuid,
      align,
      alignOffset,
      side,
      sideOffset,
      message,
      render,
      ...props
    },
    ref
  ) {
    const { isPending, enabled } = useAllow(allow, { teamUuid });

    const isPropDisabled = !!('disabled' in props && props.disabled);
    const disabled = isPending || !enabled || isPropDisabled;

    const element = useRender({
      defaultTagName: 'div',
      ref,
      render(props, state) {
        if (typeof render === 'function') {
          return render(props, state);
        }

        // Reconcile the `disabled` prop with the element's existing `disabled` prop
        // (Default behavior is to override matched props)
        const element = render;
        const elementProps = mergeProps(element.props, props, {
          disabled: element.props.disabled || state.disabled,
        });
        return cloneElement(element, elementProps);
      },
      state: { disabled },
      props: mergeProps(props, { disabled }),
    });

    return (
      <Tooltip
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        interactive
        content={
          !enabled && (
            <PermissionsTooltip
              title={message}
              allow={allow}
              teamUuid={teamUuid}
            />
          )
        }
      >
        {element}
      </Tooltip>
    );
  }
) as <TElement>(
  props: RestrictedProps & { ref?: ForwardedRef<TElement> }
) => JSX.Element;

export {
  isRoleMatch,
  useAllow,
  type UseAllowOptions,
  type UseAllowResult,
} from './access';
export { getTeamRoleLabel } from './labels';
export type { RoleRegistryKey } from './registry';
